/**
 * Unified Sign In Page
 *
 * This page serves as the unified entry point for all users:
 * - Enterprise users (Issuers/Verifiers) → API key login or signup
 * - Individual users (Subjects) → Wallet/passkey connection
 *
 * By consolidating sign-in options, we reduce confusion and provide
 * clear guidance based on user type.
 */

import {
  Shield,
  Building2,
  User,
  Wallet,
  Key,
  ArrowRight,
  ArrowLeft,
  ChevronRight,
  Fingerprint,
  AlertTriangle,
  Loader2,
  CheckCircle2,
  Eye,
  EyeOff,
  AlertCircle,
} from "lucide-react";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useAuthStore } from "../stores/auth";
import {
  useSubjectAuthStore,
  isPlatformAuthenticatorAvailable,
} from "../stores/subjectAuth";
import { useWalletStore } from "../stores/wallet";

type UserRole = "select" | "enterprise" | "subject";
type EnterpriseAction = "login" | "signup";

export function SignIn() {
  const navigate = useNavigate();
  const [role, setRole] = useState<UserRole>("select");
  const [enterpriseAction, setEnterpriseAction] = useState<EnterpriseAction>("login");
  
  // Enterprise auth
  const { login: enterpriseLogin, isLoading: enterpriseLoading, error: enterpriseError, clearError: clearEnterpriseError } = useAuthStore();
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  
  // Subject auth
  const {
    isAuthenticated: subjectAuthenticated,
    identity,
    authMethod,
    isConnecting,
    error: subjectError,
    connectWallet,
    authenticateWithPasskey,
    clearError: clearSubjectError,
  } = useSubjectAuthStore();
  const { credentials } = useWalletStore();
  const [hasPasskeySupport, setHasPasskeySupport] = useState(false);

  // Check for passkey support
  useEffect(() => {
    isPlatformAuthenticatorAvailable().then(setHasPasskeySupport);
  }, []);

  // Redirect if subject is authenticated
  useEffect(() => {
    if (subjectAuthenticated && identity && role === "subject") {
      navigate("/wallet");
    }
  }, [subjectAuthenticated, identity, role, navigate]);

  // Handle enterprise login
  const handleEnterpriseLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    clearEnterpriseError();
    
    if (!apiKey.trim()) return;

    const success = await enterpriseLogin(apiKey.trim());
    if (success) {
      navigate("/dashboard");
    }
  };

  // Handle wallet connection
  const handleConnectWallet = async () => {
    clearSubjectError();
    const success = await connectWallet();
    if (success) {
      navigate("/wallet");
    }
  };

  // Handle passkey authentication
  const handlePasskeyAuth = async () => {
    clearSubjectError();
    const success = await authenticateWithPasskey();
    if (success) {
      navigate("/wallet");
    }
  };

  const hasPasskeys = identity?.passkeys && identity.passkeys.length > 0;

  // Role Selection Screen
  if (role === "select") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col">
        {/* Header */}
        <header className="p-6">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
        </header>

        {/* Main content */}
        <main className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-2xl">
            {/* Logo */}
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-success-500 mb-4">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">Welcome to ZKCN</h1>
              <p className="text-slate-600 mt-2">Choose how you'd like to sign in</p>
            </div>

            {/* Role Selection Cards */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Enterprise Card */}
              <button
                onClick={() => setRole("enterprise")}
                className="group bg-white rounded-2xl shadow-sm border-2 border-slate-200 hover:border-primary-400 hover:shadow-lg p-6 text-left transition-all duration-200"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-primary-100 group-hover:bg-primary-200 transition-colors">
                    <Building2 className="w-7 h-7 text-primary-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-slate-900 group-hover:text-primary-700">
                      Enterprise
                    </h3>
                    <p className="text-slate-600 text-sm mt-1">
                      For businesses issuing or verifying credentials
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-primary-500 transition-colors" />
                </div>
                
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 rounded-full text-xs text-slate-600">
                      <Key className="w-3 h-3" /> API Key Login
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 rounded-full text-xs text-slate-600">
                      Dashboard Access
                    </span>
                  </div>
                </div>
              </button>

              {/* Individual Card */}
              <button
                onClick={() => setRole("subject")}
                className="group bg-white rounded-2xl shadow-sm border-2 border-slate-200 hover:border-blue-400 hover:shadow-lg p-6 text-left transition-all duration-200"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-blue-100 group-hover:bg-blue-200 transition-colors">
                    <User className="w-7 h-7 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-slate-900 group-hover:text-blue-700">
                      Individual
                    </h3>
                    <p className="text-slate-600 text-sm mt-1">
                      For users managing their own credentials
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-blue-500 transition-colors" />
                </div>
                
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 rounded-full text-xs text-slate-600">
                      <Wallet className="w-3 h-3" /> Wallet Connect
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 rounded-full text-xs text-slate-600">
                      <Fingerprint className="w-3 h-3" /> Passkey
                    </span>
                  </div>
                </div>
              </button>
            </div>

            {/* Help text */}
            <div className="mt-8 text-center">
              <p className="text-sm text-slate-500">
                Not sure which to choose?{" "}
                <Link to="/docs" className="text-primary-600 hover:underline">
                  Learn about user types
                </Link>
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Enterprise Login/Signup Screen
  if (role === "enterprise") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col">
        {/* Header */}
        <header className="p-6">
          <button 
            onClick={() => setRole("select")}
            className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to role selection
          </button>
        </header>

        {/* Main content */}
        <main className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            {/* Logo */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-success-500 mb-4">
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">Enterprise Access</h1>
              <p className="text-slate-600 mt-2">
                {enterpriseAction === "login" ? "Sign in with your API key" : "Create a new account"}
              </p>
            </div>

            {/* Toggle tabs */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-1 mb-6 flex">
              <button
                onClick={() => setEnterpriseAction("login")}
                className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                  enterpriseAction === "login"
                    ? "bg-primary-500 text-white"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => setEnterpriseAction("signup")}
                className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                  enterpriseAction === "signup"
                    ? "bg-primary-500 text-white"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Create Account
              </button>
            </div>

            {enterpriseAction === "login" ? (
              /* Login Form */
              <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
                <form onSubmit={handleEnterpriseLogin} className="space-y-6">
                  {/* Error message */}
                  {enterpriseError && (
                    <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-red-800">Authentication failed</p>
                        <p className="text-sm text-red-600 mt-1">{enterpriseError}</p>
                      </div>
                    </div>
                  )}

                  {/* API Key input */}
                  <div>
                    <label htmlFor="apiKey" className="block text-sm font-medium text-slate-700 mb-2">
                      API Key
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Key className="w-5 h-5 text-slate-400" />
                      </div>
                      <input
                        id="apiKey"
                        type={showKey ? "text" : "password"}
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        placeholder="zkcn_live_..."
                        className="block w-full pl-10 pr-12 py-3 border border-slate-300 rounded-lg 
                                 focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                                 placeholder:text-slate-400 text-slate-900"
                        disabled={enterpriseLoading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowKey(!showKey)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                      >
                        {showKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    <p className="mt-2 text-sm text-slate-500">
                      Enter your API key starting with <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">zkcn_live_</code>
                    </p>
                  </div>

                  {/* Submit button */}
                  <button
                    type="submit"
                    disabled={enterpriseLoading || !apiKey.trim()}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 
                             bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300
                             text-white font-medium rounded-lg transition-colors"
                  >
                    {enterpriseLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      <>
                        Sign in
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            ) : (
              /* Signup redirect */
              <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                    <Building2 className="w-8 h-8 text-primary-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Create Your Organization
                  </h3>
                  <p className="text-slate-600 text-sm mb-6">
                    Set up your enterprise account to start issuing and verifying credentials.
                  </p>
                  
                  <div className="space-y-3 text-left mb-6">
                    {[
                      "Free tier with 1,000 verifications/month",
                      "Instant API key generation",
                      "Full dashboard access",
                    ].map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckCircle2 className="w-4 h-4 text-success-500" />
                        {feature}
                      </div>
                    ))}
                  </div>

                  <Link
                    to="/signup"
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 
                             bg-primary-500 hover:bg-primary-600
                             text-white font-medium rounded-lg transition-colors"
                  >
                    Get Started
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            )}

            {/* Help text */}
            <div className="mt-8 text-center">
              <p className="text-sm text-slate-500">
                Need help? Check our{" "}
                <Link to="/docs" className="text-primary-600 hover:underline">
                  documentation
                </Link>{" "}
                or{" "}
                <a href="mailto:support@zkcn.org" className="text-primary-600 hover:underline">
                  contact support
                </a>
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Subject Wallet Connect Screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col">
      {/* Header */}
      <header className="p-6">
        <button 
          onClick={() => setRole("select")}
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to role selection
        </button>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Hero Section */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-blue-100 mb-6">
              <Wallet className="w-10 h-10 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Your Privacy Wallet
            </h1>
            <p className="text-gray-600">
              Securely manage your credentials and generate zero-knowledge proofs
            </p>
          </div>

          {/* Error Message */}
          {subjectError && (
            <div className="mb-6 p-4 bg-red-50 rounded-lg border border-red-200 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-700 text-sm">{subjectError}</p>
                <button
                  onClick={clearSubjectError}
                  className="text-red-600 text-sm underline mt-1"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Login Options */}
          <div className="space-y-4">
            {/* Wallet Connect - Primary */}
            <button
              onClick={handleConnectWallet}
              disabled={isConnecting}
              className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4 ${
                isConnecting
                  ? "border-gray-200 bg-gray-50 cursor-wait"
                  : "border-blue-500 bg-white hover:bg-blue-50 hover:border-blue-600"
              }`}
            >
              <div className="p-3 rounded-xl bg-blue-100">
                {isConnecting && authMethod !== "passkey" ? (
                  <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                ) : (
                  <Wallet className="w-6 h-6 text-blue-600" />
                )}
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-semibold text-gray-900">
                  Connect with Wallet
                </h3>
                <p className="text-sm text-gray-500">
                  MetaMask, WalletConnect, or any Web3 wallet
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>

            {/* Passkey Authentication */}
            {hasPasskeySupport && (
              <button
                onClick={hasPasskeys ? handlePasskeyAuth : handleConnectWallet}
                disabled={isConnecting || (!hasPasskeys && !identity)}
                className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4 ${
                  !hasPasskeys && !identity
                    ? "border-gray-200 bg-gray-50 cursor-not-allowed opacity-60"
                    : isConnecting
                    ? "border-gray-200 bg-gray-50 cursor-wait"
                    : "border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300"
                }`}
              >
                <div className="p-3 rounded-xl bg-purple-100">
                  {isConnecting && authMethod === "passkey" ? (
                    <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
                  ) : (
                    <Fingerprint className="w-6 h-6 text-purple-600" />
                  )}
                </div>
                <div className="flex-1 text-left">
                  <h3 className="font-semibold text-gray-900">
                    {hasPasskeys ? "Use Passkey" : "Passkey Login"}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {hasPasskeys
                      ? "Face ID, Touch ID, or Windows Hello"
                      : "Connect wallet first to set up passkey"}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
            )}
          </div>

          {/* Credential Count */}
          {credentials.length > 0 && (
            <div className="mt-8 p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-green-800 font-medium">
                    {credentials.length} credential{credentials.length !== 1 ? "s" : ""} found locally
                  </p>
                  <p className="text-green-700 text-sm">
                    Connect to link them to your identity
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Account Recovery */}
          <div className="mt-8 text-center">
            <Link
              to="/recovery"
              className="text-sm text-slate-600 hover:text-blue-600 transition-colors"
            >
              Having trouble? Recover your account →
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
